import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { adminApi, type Payment, type PaymentStats } from '@/lib/adminApi';
import {
  Card,
  DataTable,
  Spinner,
  EmptyState,
  Badge,
  StatTile,
  type Column,
  type BadgeTone,
} from '@/components/ui';
import { IconWallet } from '@/components/ui/icons';
import { AdminPage, AdminPageHeader, SearchBox } from '@/components/admin/shared';
import { dateTime } from '@/lib/adminFormat';
import { Pager } from '@/components/admin/Pager';
import { Drawer, Field } from '@/components/admin/Drawer';

const PAGE = 25;

// Payment kind → badge tone. A top-up is money IN (ok); a refund/dispute is
// money OUT (warn/danger). Keep in sync with admin-svc's paymentKindName.
const KIND_TONE: Record<Payment['kind'], BadgeTone> = {
  topup: 'ok',
  refund: 'warn',
  dispute: 'danger',
};

// The kind filter chips, in display order.
const KIND_FILTERS: Array<{ value: '' | Payment['kind']; label: string }> = [
  { value: '', label: 'All' },
  { value: 'topup', label: 'Top-ups' },
  { value: 'refund', label: 'Refunds' },
  { value: 'dispute', label: 'Disputes' },
];

/** Compact AUD formatter ($ + 2dp). */
function aud(n: number): string {
  return `$${(n ?? 0).toFixed(2)}`;
}

function kindBadge(p: Payment) {
  return <Badge tone={KIND_TONE[p.kind] ?? 'neutral'}>{p.kind_name || p.kind}</Badge>;
}

/** Payments — Stripe payment history (card top-ups + their refund/dispute
 *  clawbacks), read from wallet-svc's wallet_ledger (wallet-svc OWNS the money;
 *  admin-svc only READS through the shared pool). DEFENSIVE: shows a clean "not
 *  yet in Postgres" state when the wallet ledger isn't in this DB. The drawer
 *  carries a refund control (gated by payments.refund) — currently a clean
 *  "not wired yet" since wallet-svc has no admin-refund endpoint. Covers the
 *  Filament PaymentHistoryResource list/view. */
export function Payments() {
  const [q, setQ] = useState('');
  const [kind, setKind] = useState<'' | Payment['kind']>('');
  const [offset, setOffset] = useState(0);
  const [selected, setSelected] = useState<Payment | null>(null);

  const { data, isLoading, isError, error, isFetching } = useQuery({
    queryKey: ['admin', 'payments', q, kind, offset],
    queryFn: ({ signal }) => adminApi.payments({ q, kind: kind || undefined, limit: PAGE, offset }, signal),
    placeholderData: (prev) => prev,
  });

  const columns: Array<Column<Payment>> = [
    { key: 'kind', header: 'Kind', cell: (r) => kindBadge(r) },
    {
      key: 'customer',
      header: 'Customer',
      cell: (r) => (
        <div className="min-w-0">
          <div className="truncate text-sm text-slate-100">{r.customer_name}</div>
          <div className="metric text-[11px] text-muted">#{r.customer_id}</div>
        </div>
      ),
    },
    {
      key: 'reference',
      header: 'Stripe ref',
      cell: (r) => (
        <span className="metric truncate text-xs text-slate-300" title={r.reference}>
          {r.reference || '—'}
        </span>
      ),
    },
    {
      key: 'dollars',
      header: 'Amount',
      align: 'right',
      cell: (r) => (
        <span className={`metric text-sm ${r.kind === 'topup' ? 'text-ok' : 'text-danger'}`}>
          {r.kind === 'topup' ? '+' : '−'}
          {aud(r.dollars)}
        </span>
      ),
    },
    {
      key: 'km',
      header: 'km',
      align: 'right',
      cell: (r) => (
        <span className={`metric text-xs ${r.amount_km < 0 ? 'text-danger' : 'text-muted'}`}>
          {r.amount_km > 0 ? '+' : ''}
          {r.amount_km.toFixed(2)}
        </span>
      ),
    },
    { key: 'when', header: 'When', cell: (r) => <span className="text-xs text-muted">{dateTime(r.created_at)}</span> },
    {
      key: 'view',
      header: '',
      align: 'right',
      cell: (r) => (
        <button
          type="button"
          onClick={() => setSelected(r)}
          className="rounded-md border border-ink-700 px-2 py-1 text-xs text-brand-400 hover:bg-ink-800"
        >
          View
        </button>
      ),
    },
  ];

  const unavailable = data && data.available === false;

  return (
    <AdminPage>
      <AdminPageHeader
        title="Payments"
        subtitle="Stripe card top-ups and the refund / dispute clawbacks that reverse them. Open one for detail."
        right={
          <SearchBox
            value={q}
            onChange={(v) => {
              setQ(v);
              setOffset(0);
            }}
            placeholder="Search by Stripe ref / rider…"
          />
        }
      />

      {unavailable ? (
        <Card>
          <EmptyState
            title="Payment history not yet in Postgres"
            description={
              data?.note ??
              'The wallet ledger is owned by wallet-svc and is not in this Postgres yet. This page lights up automatically once it lands.'
            }
            icon={<IconWallet className="h-6 w-6" />}
          />
        </Card>
      ) : (
        <>
          {data?.stats ? <PaymentStatsRow stats={data.stats} /> : null}

          <div className="mb-3 flex flex-wrap gap-1.5">
            {KIND_FILTERS.map((f) => {
              const active = f.value === kind;
              return (
                <button
                  key={f.value || 'all'}
                  type="button"
                  onClick={() => {
                    setKind(f.value);
                    setOffset(0);
                  }}
                  className={
                    active
                      ? 'rounded-md bg-brand-500/20 px-2.5 py-1 text-xs font-medium text-brand-300 ring-1 ring-inset ring-brand-500/40'
                      : 'rounded-md bg-ink-850 px-2.5 py-1 text-xs font-medium text-slate-300 ring-1 ring-inset ring-ink-700 hover:bg-ink-800'
                  }
                >
                  {f.label}
                </button>
              );
            })}
          </div>

          <Card flush>
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Spinner label="Loading payments…" />
              </div>
            ) : isError ? (
              <EmptyState title="Couldn't load payments" description={(error as Error)?.message} />
            ) : (
              <>
                <DataTable<Payment>
                  columns={columns}
                  rows={data?.items ?? []}
                  rowKey={(r) => r.ref}
                  stickyHeader
                  empty={
                    <EmptyState
                      title="No payments"
                      description="No Stripe payments match this filter."
                      icon={<IconWallet className="h-6 w-6" />}
                    />
                  }
                />
                <Pager total={data?.total ?? 0} offset={offset} pageSize={PAGE} onOffset={setOffset} fetching={isFetching} />
              </>
            )}
          </Card>
        </>
      )}

      {selected ? <PaymentDrawer payment={selected} onClose={() => setSelected(null)} /> : null}
    </AdminPage>
  );
}

/** The headline totals above the table: top-ups in, clawbacks out, net settled. */
function PaymentStatsRow({ stats }: { stats: PaymentStats }) {
  return (
    <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
      <StatTile label="Top-ups" value={aud(stats.topup_dollars)} hint={`${stats.topup_count} payment${stats.topup_count === 1 ? '' : 's'}`} />
      <StatTile label="Refunds" value={aud(stats.refund_dollars)} hint={`${stats.refund_count} clawback${stats.refund_count === 1 ? '' : 's'}`} />
      <StatTile label="Disputes" value={aud(stats.dispute_dollars)} hint={`${stats.dispute_count} chargeback${stats.dispute_count === 1 ? '' : 's'}`} />
      <StatTile label="Net settled" value={aud(stats.net_dollars)} hint="top-ups − refunds − disputes" />
    </div>
  );
}

/** PaymentDrawer — the full payment detail. The list row already carries every
 *  field, so no extra fetch is needed for the body. */
function PaymentDrawer({ payment: p, onClose }: { payment: Payment; onClose: () => void }) {
  return (
    <Drawer
      title={p.kind_name || p.kind}
      subtitle={p.reference ? `Stripe ${p.reference}` : `ledger #${p.ledger_id}`}
      onClose={onClose}
    >
      <div className="space-y-4">
        <div>
          <Field label="Kind" value={kindBadge(p)} />
          <Field label="Stripe reference" value={<span className="metric">{p.reference || '—'}</span>} />
          <Field
            label="Amount (AUD)"
            value={
              <span className={p.kind === 'topup' ? 'text-ok' : 'text-danger'}>
                {p.kind === 'topup' ? '+' : '−'}
                {aud(p.dollars)}
              </span>
            }
          />
          <Field
            label="km effect"
            value={
              <span className={p.amount_km < 0 ? 'text-danger' : 'text-slate-200'}>
                {p.amount_km > 0 ? '+' : ''}
                {p.amount_km.toFixed(2)} km
              </span>
            }
          />
          <Field label="Customer" value={`${p.customer_name} (#${p.customer_id})`} />
          {p.phone ? <Field label="Phone" value={<span className="metric">{p.phone}</span>} /> : null}
          {p.email ? <Field label="Email" value={p.email} /> : null}
          <Field label="Ledger ref" value={<span className="metric text-xs">{p.ref}</span>} />
          <Field label="When" value={dateTime(p.created_at)} />
        </div>
      </div>
    </Drawer>
  );
}
